import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Descriptions, Form, InputNumber, TimePicker, Modal, Select, Space, Switch, Table, Tag, } from "antd";
import moment from "moment";
import { getClass, getClassSlots, ListEmployee, updateClassSlot, } from "../../constants/API/API";
import { useParams } from "react-router";
import { Severty, ShowToast } from "../../helper/toast";
import { BankOutlined, CalendarOutlined, ClockCircleOutlined, EditOutlined, NumberOutlined, UserOutlined, } from "@ant-design/icons";
import callApi from "../../constants/API/CallApi";

const ClassViewPage = () => {
  const [slots, setSlots] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [classLoading, setClassLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [classData, setClassData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [form] = Form.useForm();
  const { id } = useParams();

  const fetchClassData = useCallback(async () => {
    setClassLoading(true);
    try {
      const response = await getClass(id);
      if (response.status === 200 && response.data) {
        setClassData(response.data);
        getTrainers(response.data?.branchId?._id);
      }
    } catch (error) {
      ShowToast(error.message || "Failed to fetch class data", Severty.ERROR);
    } finally {
      setClassLoading(false);
    }
  }, [id]);

  const fetchClassSlots = useCallback(async () => {
    setSlotsLoading(true);
    try {
      const response = await getClassSlots({ classId: id });
      if (response.status === 200 && response.data) {
        setSlots(response?.data?.data || []);
      }
    } catch (error) {
      ShowToast(error.message || "Failed to fetch class data", Severty.ERROR);
    } finally {
      setSlotsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClassData();
    fetchClassSlots();
  }, [fetchClassData, fetchClassSlots]);

  const DAYS_OF_WEEK = [
    { label: "Sunday", value: "SUNDAY" },
    { label: "Monday", value: "MONDAY" },
    { label: "Tuesday", value: "TUESDAY" },
    { label: "Wednesday", value: "WEDNESDAY" },
    { label: "Thursday", value: "THURSDAY" },
    { label: "Friday", value: "FRIDAY" },
    { label: "Saturday", value: "SATURDAY" },
  ];

  const openEditModal = (slot) => {
    setEditingSlot(slot);

    form.setFieldsValue({
      day: slot.day,
      trainerId: slot?.trainer?._id || slot?.trainerId,
      capacity: slot.capacity,
      isActive: slot.isActive,
      time: [
        moment(slot.startTime, "HH:mm"),
        moment(slot.endTime, "HH:mm"),
      ],
    });
    setIsModalOpen(true);
  };

  const handleUpdateSlot = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        classId: id,
        slotId: editingSlot._id,
        day: values.day,
        trainerId: values.trainerId,
        capacity: values.capacity,
        isActive: values.isActive,
        startTime: values.time[0].format("HH:mm"),
        endTime: values.time[1].format("HH:mm"),
      };

      callApi({
        callback: updateClassSlot,
        payload,
        onSuccess: () => {
          ShowToast("Slot updated successfully!", Severty.SUCCESS);
          setIsModalOpen(false);
          fetchClassSlots();
        },
        onError: (err) => ShowToast(err, Severty.ERROR),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getTrainers = (id) => {
    callApi({
      callback: ListEmployee,
      payload: {
        role: "trainer",
        branchId: id,
      },
      dependencies: [],
      shouldCallApi: true,
      onSuccess: ({ data }) => {
        setTrainers(data?.list || []);
      },
    });
  };

  const columns = [
    {
      title: "Trainer",
      dataIndex: "trainer",
      key: "trainer",
      render: (text, record) => record?.trainer?.name || "-",
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      render: (text, record) => record?.day || "-",
    },
    {
      title: "Time Slot",
      dataIndex: "slot",
      key: "slot",
      render: (text, record) => record?.startTime + "-" + record?.endTime,
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (text, record) => record?.capacity || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <Tag>{record?.isActive ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="default" onClick={() => openEditModal(record)}>
            <EditOutlined />
          </Button>
          {/* <Button type="primary" onClick={() => openEditModal(record)}>
            <ClockCircleOutlined />
          </Button> */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="default"
        className="mb-2"
        style={{ color: "black" }}
        onClick={() => window.history.back()}
      >
        Back
      </Button>

      <Card loading={classLoading} title="Class Overview" className="mb-4">
        <Descriptions
          bordered
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
          labelStyle={{
            fontWeight: 500,
            backgroundColor: "#fafafa",
            whiteSpace: "nowrap",
            minWidth: "150px",
            width: "40%",
          }}
          contentStyle={{
            backgroundColor: "#fff",
          }}
        >
          <Descriptions.Item
            label={
              <Space>
                <BankOutlined /> Class Name
              </Space>
            }
          >
            {classData.name}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <BankOutlined /> Branch Name
              </Space>
            }
          >
            {classData.branchId?.name}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <UserOutlined /> Age Limit
              </Space>
            }
          >
            {classData.ageLimitStart != null || classData.ageLimitEnd != null ? (
              <Tag
                color="blue"
                style={{
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "14px",
                }}
              >
                {classData.ageLimitStart != null && classData.ageLimitEnd != null
                  ? `${classData.ageLimitStart} - ${classData.ageLimitEnd} years`
                  : classData.ageLimitStart != null
                  ? `${classData.ageLimitStart}+ years`
                  : `Up to ${classData.ageLimitEnd} years`}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <ClockCircleOutlined /> Class Status
              </Space>
            }
          >
            <Tag
              color={classData.isActive ? "success" : "error"}
              style={{
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "14px",
              }}
            >
              {classData.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <CalendarOutlined /> Created At
              </Space>
            }
          >
            {moment(classData.createdAt).format("MMMM Do YYYY, h:mm:ss a")}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <CalendarOutlined /> Last Updated
              </Space>
            }
          >
            {moment(classData.updatedAt).format("MMMM Do YYYY, h:mm:ss a")}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <NumberOutlined /> UID
              </Space>
            }
          >
            <Tag style={{ fontFamily: "monospace", fontSize: "13px" }}>
              {classData.uid}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Class Schedules" className="mb-4 mt-4">
        <div className="table-responsive customPagination">

          <Table
            loading={slotsLoading}
            columns={columns}
            dataSource={slots || []}
            pagination={false}
            rowKey="_id"
            bordered
            size="middle"
          />
        </div>
      </Card>

      <Modal
        title="Edit Slot"
        open={isModalOpen}
        onOk={handleUpdateSlot}
        onCancel={() => setIsModalOpen(false)}
        okText="Update"
      >
        <Form form={form} layout="vertical">

          <Form.Item
            label="Day"
            name="day"
            rules={[{ required: true, message: "Please select day" }]}
          >
            <Select placeholder="Select Day">
              {DAYS_OF_WEEK.map((day) => (
                <Select.Option key={day.value} value={day.value}>
                  {day.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Time Slot"
            name="time"
            rules={[{ required: true, message: "Please select time range" }]}
          >
            <TimePicker.RangePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Trainer"
            name="trainerId"
            rules={[{ required: true, message: "Please select trainer" }]}
          >
            <Select placeholder="Select Trainer">
              {trainers.map((trainer) => (
                <Select.Option key={trainer._id} value={trainer._id}>
                  {trainer.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Class Capacity"
            name="capacity"
            rules={[{ required: true, message: "Please enter capacity" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Active Status"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

        </Form>
      </Modal>


    </div>
  );
};

export default ClassViewPage;
